import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, offlineService } from '../services';
import { startProactiveRefresh, stopProactiveRefresh } from '../services/api';
import stocktakeOfflineService from '../services/stocktakeOfflineService';

const AuthContext = createContext(null);

// Helper to check if user has a specific role (multi-role aware)
const hasRole = (user, roleName) => {
  if (!user) return false;
  // Check new roles array first
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.includes(roleName);
  }
  // Fallback to legacy single role
  return user.role === roleName;
};

// Helper to check if user has any of the given roles
const hasAnyRole = (user, roleNames) => {
  if (!user) return false;
  return roleNames.some(role => hasRole(user, role));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOfflineLogin, setIsOfflineLogin] = useState(false);

  useEffect(() => {
    // Check for stored user on mount
    const storedUser = authService.getStoredUser();
    if (storedUser) {
      setUser(storedUser);
      
      // Ensure offline_last_user is always up to date for offline resume
      try {
        localStorage.setItem('offline_last_user', JSON.stringify({
          user: storedUser,
          authenticatedAt: new Date().toISOString(),
        }));
      } catch { /* quota */ }
      
      // If online, refresh user data from server to get latest profile_photo_url etc.
      if (navigator.onLine && authService.isAuthenticated()) {
        authService.refreshUser().then(freshUser => {
          if (freshUser) {
            setUser(freshUser);
          }
        }).catch(err => {
          console.error('Failed to refresh user on mount:', err);
        });
      }
    }
    setLoading(false);

    // Listen for session-expired events from the API interceptor
    const handleSessionExpired = () => {
      // On count pages, don't redirect — the count page shows its own re-auth overlay
      // to preserve offline count data
      if (window.location.pathname.startsWith('/count/')) {
        return;
      }
      setUser(null);
      setIsOfflineLogin(false);
      // offline_last_user is preserved — the login page will show the resume button
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  const login = useCallback(async (email, password) => {
    // Check if online
    if (navigator.onLine) {
      try {
        // Try online login first
        const { user } = await authService.login(email, password);
        setUser(user);
        setIsOfflineLogin(false);
        startProactiveRefresh();
        
        // Store credentials for offline use
        await offlineService.storeOfflineCredentials(email, password, user);
        
        // Also store as "last authenticated user" for offline resume (no password needed)
        try {
          localStorage.setItem('offline_last_user', JSON.stringify({
            user,
            authenticatedAt: new Date().toISOString(),
          }));
        } catch { /* quota */ }
        
        // Auto-sync any pending offline sessions after successful login
        try {
          const syncResult = await stocktakeOfflineService.syncAllPendingSessions();
          if (syncResult.synced > 0) {
            console.log(`[Auth] Auto-synced ${syncResult.synced} offline session(s) after login`);
          }
        } catch (syncErr) {
          console.warn('[Auth] Auto-sync after login failed:', syncErr);
        }
        
        // Cache data for auditors (multi-role aware)
        if (hasRole(user, 'auditor')) {
          // Get auditor ID from the user record
          // The auditor record will be fetched and cached during data sync
          offlineService.cacheScheduleData(user.auditor_id || user.id).catch(console.error);
        }
        
        return { user, isOffline: false };
      } catch (error) {
        throw error;
      }
    } else {
      // Attempt offline login
      const result = await offlineService.attemptOfflineLogin(email, password);
      
      if (result.success) {
        setUser(result.user);
        setIsOfflineLogin(true);
        // Store in localStorage for session persistence
        localStorage.setItem('user', JSON.stringify(result.user));
        return { user: result.user, isOffline: true };
      } else {
        throw new Error(result.error);
      }
    }
  }, []);

  const register = async (data) => {
    if (!navigator.onLine) {
      throw new Error('Registration requires an internet connection.');
    }
    const { user } = await authService.register(data);
    setUser(user);
    setIsOfflineLogin(false);
    return user;
  };

  // Resume offline session without password (uses cached last user or IndexedDB fallback)
  const resumeOffline = useCallback(async () => {
    // Try 1: localStorage cached user
    try {
      const raw = localStorage.getItem('offline_last_user');
      if (raw) {
        const { user: cachedUser } = JSON.parse(raw);
        if (cachedUser) {
          setUser(cachedUser);
          setIsOfflineLogin(true);
          localStorage.setItem('user', JSON.stringify(cachedUser));
          return { success: true, user: cachedUser };
        }
      }
    } catch { /* ignore */ }

    // Try 2: Check IndexedDB for stocktake packages — if any exist, user was previously authenticated
    try {
      const db = await new Promise((resolve, reject) => {
        const req = indexedDB.open('abbeyzone_offline');
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      const storeNames = Array.from(db.objectStoreNames);
      if (storeNames.includes('stocktake_packages')) {
        const pkgs = await new Promise((resolve) => {
          const tx = db.transaction('stocktake_packages', 'readonly');
          const store = tx.objectStore('stocktake_packages');
          const ga = store.getAll();
          ga.onsuccess = () => resolve(ga.result || []);
          ga.onerror = () => resolve([]);
        });
        db.close();
        if (pkgs.length > 0) {
          // Construct minimal offline user from package data
          const pkg = pkgs[0];
          const minimalUser = {
            id: pkg.package?.session?.auditor_id || 0,
            email: 'offline@device',
            first_name: 'Offline',
            last_name: 'User',
            roles: [{ role: { name: 'auditor' } }],
          };
          setUser(minimalUser);
          setIsOfflineLogin(true);
          localStorage.setItem('user', JSON.stringify(minimalUser));
          return { success: true, user: minimalUser };
        }
      } else {
        db.close();
      }
    } catch { /* IndexedDB unavailable */ }

    return { success: false };
  }, []);

  const logout = useCallback(async (clearOfflineData = false) => {
    const currentEmail = user?.email;
    
    // Always preserve offline_last_user on normal logout (so offline resume works)
    if (user && !clearOfflineData) {
      try {
        localStorage.setItem('offline_last_user', JSON.stringify({
          user,
          authenticatedAt: new Date().toISOString(),
        }));
      } catch { /* quota */ }
    }
    
    try {
      if (navigator.onLine) {
        await authService.logout();
      }
    } finally {
      stopProactiveRefresh();
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      
      if (clearOfflineData && currentEmail) {
        await offlineService.clearOfflineCredentials(currentEmail);
        localStorage.removeItem('offline_last_user');
      }
      
      setUser(null);
      setIsOfflineLogin(false);
    }
  }, [user]);

  // Clear offline data and logout (for "Log out on this device" feature)
  const logoutAndClearDevice = useCallback(async () => {
    await logout(true);
  }, [logout]);

  const value = {
    user,
    loading,
    login,
    resumeOffline,
    register,
    logout,
    logoutAndClearDevice,
    isAuthenticated: !!user,
    isOfflineLogin,
    // Password change required flag
    mustChangePassword: user?.must_change_password || false,
    // Multi-role aware checks
    isAdmin: hasRole(user, 'admin'),
    isSeniorScheduler: hasRole(user, 'senior_scheduler'),
    isScheduler: hasRole(user, 'scheduler') || hasRole(user, 'senior_scheduler'), // Senior Scheduler inherits Scheduler
    isAuditor: hasRole(user, 'auditor'),
    // Permission checks - user with ANY of these roles gets permission
    // Senior Scheduler inherits all Scheduler permissions
    canManageSites: hasAnyRole(user, ['admin', 'senior_scheduler', 'scheduler']),
    canManageLogos: hasAnyRole(user, ['admin', 'senior_scheduler', 'scheduler', 'auditor']),
    canViewSites: hasAnyRole(user, ['admin', 'senior_scheduler', 'scheduler', 'auditor']),  // Phase 1A: Auditors can view
    canEditSiteAuditorFields: hasAnyRole(user, ['admin', 'senior_scheduler', 'scheduler', 'auditor']),  // Phase 1A: Budget, stockholding, contacts
    canManageAuditors: hasRole(user, 'admin'),
    canManageJobs: hasAnyRole(user, ['admin', 'senior_scheduler', 'scheduler']),
    // Auditors cannot complete jobs - only Scheduler/Admin/Senior Scheduler
    canCompleteJobs: hasAnyRole(user, ['admin', 'senior_scheduler', 'scheduler']),
    // Batch approval permissions - Senior Scheduler only (not regular Scheduler)
    canApproveBatches: hasAnyRole(user, ['admin', 'senior_scheduler']),
    canReviewBatches: hasAnyRole(user, ['admin', 'senior_scheduler']),
    canReturnBatches: hasAnyRole(user, ['admin', 'senior_scheduler']),
    canDeleteBatches: hasAnyRole(user, ['admin', 'senior_scheduler']),
    canSaveAsApproved: hasAnyRole(user, ['admin', 'senior_scheduler']),
    // iter416 — Auditors flagged with `can_directly_publish_reports` should be
    // treated like Admin/Scheduler for the publish action specifically. The
    // backend already honours the flag (iter404); the frontend was still
    // gating the "Publish" button strictly on role, so trusted auditors saw
    // only "Request Publish" / "Submit for Approval" and their reports went
    // through approval anyway. This derived flag fixes that.
    canDirectlyPublishReports: (
        hasAnyRole(user, ['admin', 'senior_scheduler', 'scheduler'])
        || (hasRole(user, 'auditor') && !!user?.can_directly_publish_reports)
    ),
    // Helper functions exposed for components
    hasRole: (role) => hasRole(user, role),
    hasAnyRole: (roles) => hasAnyRole(user, roles),
    // Refresh user data from server (useful after profile updates)
    refreshUser: async () => {
      if (navigator.onLine && authService.isAuthenticated()) {
        const freshUser = await authService.refreshUser();
        if (freshUser) {
          setUser(freshUser);
          return freshUser;
        }
      }
      return null;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
